import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { getFunctions, httpsCallable } from "firebase/functions";

import app from "../firebase";

import "./OrderStatus.css";

function formatMoney(value) {
  return `$${Number(value || 0).toFixed(2)}`;
}

function formatDate(value) {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

/*
 * Determines whether an item actually includes framing.
 *
 * "Unframed" and other no-frame values are treated as
 * NOT framed.
 */
function itemHasFraming(item) {
  if (!item) {
    return false;
  }

  const frame = item.frame;

  if (!frame) {
    return false;
  }

  const frameValue = String(frame).trim().toLowerCase();

  const noFrameValues = [
    "",
    "none",
    "no",
    "no frame",
    "no framing",
    "unframed",
    "not framed",
    "false",
    "null",
    "undefined",
    "n/a",
    "na",
  ];

  return !noFrameValues.includes(frameValue);
}

const STATUS_STEPS = [
  {
    key: "PAID",
    label: "ORDER CONFIRMED",
    description: "Your payment has been received and your order is confirmed.",
  },
  {
    key: "IN PRODUCTION",
    label: "IN PRODUCTION",
    description: "Your order is currently being prepared.",
  },
  {
    key: "PRINTING",
    label: "PRINTING",
    description: "Your photograph is currently being printed.",
  },
  {
    key: "FRAMING",
    label: "FRAMING",
    description: "Your print is currently being framed.",
  },
  {
    key: "READY",
    label: "READY",
    description: "Your order is finished and ready to be shipped.",
  },
  {
    key: "SHIPPED",
    label: "SHIPPED",
    description: "Your order has shipped.",
  },
  {
    key: "DELIVERED",
    label: "DELIVERED",
    description: "Your order has been delivered.",
  },
];

export default function OrderStatus() {
  const location = useLocation();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;
    let retryTimer = null;

    async function loadOrder(attempt = 0) {
      if (!mounted) {
        return;
      }

      setLoading(true);
      setError("");

      const params = new URLSearchParams(location.search);

      const token = params.get("token");
      const sessionId = params.get("session_id");

      if (!token && !sessionId) {
        if (mounted) {
          setError("No order tracking link was provided.");
          setLoading(false);
        }

        return;
      }

      try {
        const functions = getFunctions(app);
        const getOrderStatus = httpsCallable(functions, "getOrderStatus");

        const result = await getOrderStatus({
          token: token || "",
          sessionId: sessionId || "",
        });

        if (mounted) {
          setOrder(result.data);
          setLoading(false);
        }
      } catch (err) {
        console.error("Order status error:", err);

        if (!mounted) {
          return;
        }

        /*
         * Stripe can redirect the customer to this page before
         * the webhook has finished creating the Firestore order.
         *
         * Retry several times when using session_id.
         */
        const isNotFound = err?.code === "functions/not-found";

        const isInvalidArgument = err?.code === "functions/invalid-argument";

        if (sessionId && isNotFound && attempt < 5) {
          retryTimer = setTimeout(() => {
            loadOrder(attempt + 1);
          }, 2000);

          return;
        }

        if (isNotFound || isInvalidArgument) {
          setError("We couldn't find an order with this tracking link.");
        } else {
          setError("We couldn't load your order right now. Please try again.");
        }

        setLoading(false);
      }
    }

    loadOrder();

    return () => {
      mounted = false;

      if (retryTimer) {
        clearTimeout(retryTimer);
      }
    };
  }, [location.search]);

  if (loading) {
    return (
      <main className="order-status-page">
        <div className="order-status-container">
          <p className="order-status-loading">Loading order...</p>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="order-status-page">
        <div className="order-status-container">
          <h1>ORDER NOT FOUND</h1>

          <div className="order-status-rule" />

          <p className="order-status-error">{error}</p>

          <p className="order-status-help">
            Please make sure you are using the order tracking link from your
            confirmation email.
          </p>
        </div>
      </main>
    );
  }

  if (!order) {
    return null;
  }

  const items = Array.isArray(order.items) ? order.items : [];

  const shippingAddress = order.shippingAddress || {};

  /*
   * Only show FRAMING if at least one item has an
   * actual framing option.
   *
   * Example:
   * frame: "Unframed" -> false
   * frame: "Black Oak" -> true
   */
  const hasFraming = items.some((item) => itemHasFraming(item));

  /*
   * Remove FRAMING completely when the order is unframed.
   */
  const visibleStatusSteps = STATUS_STEPS.filter((step) => {
    if (step.key === "FRAMING") {
      return hasFraming;
    }

    return true;
  });

  /*
   * Find the current status within the visible steps.
   */
  let currentStatusIndex = visibleStatusSteps.findIndex(
    (step) => step.key === order.status,
  );

  /*
   * Safety check:
   * If the backend says FRAMING but the order has no framing,
   * treat the order as being at PRINTING.
   */
  if (order.status === "FRAMING" && !hasFraming) {
    currentStatusIndex = visibleStatusSteps.findIndex(
      (step) => step.key === "PRINTING",
    );
  }

  currentStatusIndex = Math.max(currentStatusIndex, 0);

  return (
    <main className="order-status-page">
      <div className="order-status-container">
        {/* HEADER */}
        <header className="order-status-header">
          <p className="order-status-eyebrow">OCTAVIO PHOTO</p>

          <h1>ORDER #{order.orderNumber}</h1>

          {order.createdAt && (
            <p className="order-status-date">
              Placed {formatDate(order.createdAt)}
            </p>
          )}
        </header>

        <div className="order-status-rule" />

        {/* ORDER PROGRESS */}
        <section className="order-status-section order-status-progress-section">
          <p className="order-status-label">ORDER STATUS</p>

          <div className="order-status-progress">
            {visibleStatusSteps.map((step, index) => {
              const isComplete = index < currentStatusIndex;

              const isCurrent = index === currentStatusIndex;

              return (
                <div
                  key={step.key}
                  className={[
                    "order-status-step",
                    isComplete ? "is-complete" : "",
                    isCurrent ? "is-current" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  <div className="order-status-step-marker">
                    {isComplete ? "✓" : index + 1}
                  </div>

                  <div className="order-status-step-content">
                    <p className="order-status-step-label">{step.label}</p>

                    {isCurrent && (
                      <p className="order-status-step-description">
                        {step.description}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <div className="order-status-rule" />

        {/* ORDER ITEMS */}
        <section className="order-status-section">
          <p className="order-status-label">YOUR ORDER</p>

          <div className="order-status-items">
            {items.map((item, index) => (
              <div
                className="order-status-item"
                key={`${item.printId || item.title || "item"}-${index}`}
              >
                <div>
                  <h3>{item.title || "Print"}</h3>

                  {item.size && <p>{item.size}</p>}

                  {(item.paper || item.frame) && (
                    <p>
                      {item.paper || ""}
                      {item.paper && item.frame ? " · " : ""}
                      {item.frame || ""}
                    </p>
                  )}
                </div>

                <div className="order-status-quantity">
                  × {item.quantity || 1}
                </div>
              </div>
            ))}
          </div>
        </section>

        <div className="order-status-rule" />

        {/* SHIPPING */}
        <section className="order-status-section">
          <p className="order-status-label">SHIPPING TO</p>

          <p className="order-status-address">
            {shippingAddress.name && (
              <>
                {shippingAddress.name}
                <br />
              </>
            )}

            {shippingAddress.city &&
              shippingAddress.state &&
              `${shippingAddress.city}, ${shippingAddress.state}`}

            {shippingAddress.zip && ` ${shippingAddress.zip}`}
          </p>
        </section>

        {/* TRACKING NUMBER */}
        {order.trackingNumber && (
          <>
            <div className="order-status-rule" />

            <section className="order-status-section">
              <p className="order-status-label">TRACKING</p>

              <p className="order-status-tracking">{order.trackingNumber}</p>
            </section>
          </>
        )}

        <div className="order-status-rule" />

        {/* TOTALS */}
        <section className="order-status-totals">
          <div>
            <span>SUBTOTAL</span>
            <span>{formatMoney(order.subtotal)}</span>
          </div>

          <div>
            <span>SHIPPING</span>
            <span>{formatMoney(order.shipping)}</span>
          </div>

          <div>
            <span>TAX</span>
            <span>{formatMoney(order.tax)}</span>
          </div>

          <div className="order-status-total">
            <span>TOTAL</span>
            <span>{formatMoney(order.total)}</span>
          </div>
        </section>

        {/* FOOTER */}
        <footer className="order-status-footer">
          <p>Thank you for supporting the work.</p>

          <p>OCTAVIO PHOTO</p>
        </footer>
      </div>
    </main>
  );
}
